import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as _ from 'lodash';
import { SortOrder } from '../../utils/enums/sort-order.enum';
import { InviteStatus } from '../../utils/enums/invite-status.enum';

export interface Invitation {
  id: string | null;
  email: string | '';
  status: InviteStatus | '';
  createdAt: string | null;
}
export interface IPermission {
  permissionId: number;
  permission: string;
  description: string;
  dependencies?: number[];
}

export interface IInvitePayload {
  emails: string[];
  permissions: number[];
}

export interface IGetInvitationsParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: SortOrder;
}

export interface IGetInvitationsResponse {
  data: Invitation[];
  total: number;
  page: number;
  limit: number;
}

export interface IInvite {}

export interface IInviteSlice {
  componentStatus: { [key: string]: undefined | string | boolean | number };
  invites: Invitation[];
  permissions: IPermission[] | null;
  inviteStatus: InviteStatus | string | null;
  isInformByUser?: boolean;
  inviteError?: string | null;
  invitationLoading?: boolean;
  responseStatus?: number;
  total: number;
  recallInvitationLoading: boolean;
  recallInvitationStatus: number | null;
}

const initialState: IInviteSlice = {
  componentStatus: {},
  invites: [],
  permissions: [],
  inviteStatus: null,
  inviteError: null,
  invitationLoading: false,
  total: 0,
  recallInvitationLoading: false,
  recallInvitationStatus: null,
};

const inviteSlice = createSlice({
  name: 'invite',
  initialState,
  reducers: {
    setComponentStatus(
      state: IInviteSlice,
      action: PayloadAction<{ [key: string]: undefined | string | boolean | number }>,
    ) {
      state.componentStatus = { ...state.componentStatus, ...action.payload };
    },

    clearComponentStatus(state, action: PayloadAction<string[] | undefined>) {
      if (action.payload?.length) {
        state.componentStatus = _.omit(state.componentStatus, action.payload);
      } else {
        state.componentStatus = {};
      }
    },

    setPermissions(state, action: PayloadAction<IPermission[]>) {
      state.permissions = action.payload;
      state.inviteError = null;
    },
    getInvitationsAsync(_state: IInviteSlice, _action: PayloadAction<IGetInvitationsParams>) {},
    setInvitations(state: IInviteSlice, action: PayloadAction<{ data: Invitation[]; total: number }>) {
      state.invites = [...action.payload.data];
      state.total = action.payload.total;
    },
    resetGetInvitations(state: IInviteSlice) {
      state.invites = [];
      state.total = 0;
    },
    setInviteStatus(state, action: PayloadAction<string | null>) {
      state.inviteStatus = action.payload;
    },

    setInviteError(state, action: PayloadAction<string | null>) {
      state.inviteError = action.payload;
    },

    setInvitationLoading(state, action: PayloadAction<boolean>) {
      state.invitationLoading = action.payload;
    },

    getPermissionsAsync(state) {
      state.inviteError = null;
    },
    inviteSubordinateAsync(state, _action: PayloadAction<IInvitePayload>) {
      state.invitationLoading = true;
      state.inviteError = null;
    },

    inviteSubordinateSuccess(state) {
      state.invitationLoading = false;
      state.inviteStatus = 'Invitation sent successfully';
    },
    inviteSubordinateFailure(state, action: PayloadAction<string>) {
      state.invitationLoading = false;
      state.inviteError = action.payload;
    },

    getValidateInviteAsync(_state, _action: PayloadAction<string>) {},
    getRecallInviteAsync(_state, _action: PayloadAction<string>) {},

    setInviteValidity(state, action: PayloadAction<{ data: any; status: any }>) {
      state.inviteStatus = action.payload.data.status;
      state.isInformByUser = action.payload.data.isInformByUser;
      state.responseStatus = action.payload.status;
    },

    resetInviteState(state) {
      state.inviteStatus = null;
      state.inviteError = null;
      state.invitationLoading = false;
    },
    recallInvitationAsync(_state, _action: PayloadAction<string>) {},
    recallInvitationSuccess(state, action: PayloadAction<{ id: string }>) {
      state.recallInvitationLoading = false;
      state.recallInvitationStatus = 200;
      state.inviteStatus = 'Invite Recalled!';

      const index = state.invites.findIndex((invite) => invite.id === action.payload.id);
      if (index !== -1) {
        state.invites[index].status = InviteStatus.RECALLED;
      }
    },
    recallInvitationFailure(state, action: PayloadAction<string>) {
      state.recallInvitationLoading = false;
      state.inviteError = action.payload;
    },
  },
});

export const { actions: inviteActions, reducer: inviteReducer } = inviteSlice;
